import React, { useState, useEffect, useRef } from 'react';
import { alertsAPI } from '../services/api';
import './ResolutionPlan.css';

const RISK_COLOR = {
  low:      'var(--color-success-500)',
  medium:   'var(--accent)',
  high:     'var(--color-danger-500)',
  critical: 'var(--color-danger-700)',
};

const RISK_BG = {
  low:      'var(--color-success-50)',
  medium:   'var(--color-warning-50)',
  high:     'var(--color-danger-50)',
  critical: 'var(--color-danger-50)',
};

const ACTION_ICON = {
  kubectl_get:      '🔍',
  kubectl_describe: '📋',
  kubectl_logs:     '📜',
  kubectl_delete:   '🗑',
  kubectl_patch:    '🔧',
  kubectl_scale:    '⚖️',
  kubectl_rollout:  '🔄',
  kubectl_apply:    '📥',
  kubectl_exec:     '💻',
  manual:           '👤',
};

const SAFE_ACTIONS = new Set([
  'kubectl_get', 'kubectl_describe', 'kubectl_logs', 'kubectl_exec'
]);

// ── Step status badge ─────────────────────────────────────────────────────────

const StepBadge = ({ status }) => {
  const cfg = {
    pending:   { bg: 'var(--surface-2)', color: 'var(--ink-2)', label: 'Pending' },
    approved:  { bg: 'var(--brand-soft)', color: 'var(--brand)', label: 'Approved' },
    executing: { bg: 'var(--color-warning-50)', color: 'var(--color-warning-700)', label: 'Running…' },
    done:      { bg: 'var(--color-success-50)', color: 'var(--color-success-700)', label: 'Done ✓' },
    failed:    { bg: 'var(--color-danger-50)', color: 'var(--color-danger-700)', label: 'Failed ✗' },
    skipped:   { bg: 'var(--surface-2)', color: 'var(--muted)', label: 'Skipped' },
  }[status] || { bg: 'var(--surface-2)', color: 'var(--ink-2)', label: status };

  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      padding: '2px 8px', borderRadius: 10,
      fontSize: 11, fontWeight: 600
    }}>
      {cfg.label}
    </span>
  );
};

// ── Single step card ──────────────────────────────────────────────────────────

const StepCard = ({ step, alertId, onStepUpdated, planStatus }) => {
  const [loading, setLoading]   = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [stepError, setStepError] = useState(null);
  const pollRef                 = useRef(null);
  const riskColor = RISK_COLOR[step.risk_level] || 'var(--muted)';
  const riskBg    = RISK_BG[step.risk_level]    || 'var(--surface-2)';
  const isManual   = step.action_type === 'manual';
  const isReadOnly = SAFE_ACTIONS.has(step.action_type);
  const canExecute = !isManual && (step.status === 'approved' || (isReadOnly && step.status === 'pending'));
  const canMarkDone = isManual && ['pending', 'approved'].includes(step.status);
  const canApprove = step.status === 'pending' && !isReadOnly && !isManual;
  const canSkip    = ['pending', 'approved', 'failed'].includes(step.status);

  // Auto-poll while step is executing so UI doesn't get stuck
  useEffect(() => {
    if (step.status === 'executing') {
      let attempts = 0;
      const poll = async () => {
        attempts++;
        onStepUpdated();
        if (attempts < 60) {
          pollRef.current = setTimeout(poll, 2000);
        }
      };
      pollRef.current = setTimeout(poll, 2000);
    }
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [step.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = async () => {
    setLoading(true);
    setStepError(null);
    try {
      await alertsAPI.approveStep(alertId, step.step_id);
      onStepUpdated();
    } catch (e) {
      setStepError('Approve failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    setLoading(true);
    setStepError(null);
    try {
      await alertsAPI.executeStep(alertId, step.step_id);
      onStepUpdated();
    } catch (e) {
      setStepError('Execute failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  // For manual steps — calls executeStep which marks them done immediately on the backend
  const handleMarkDone = async () => {
    setLoading(true);
    setStepError(null);
    try {
      await alertsAPI.executeStep(alertId, step.step_id);
      onStepUpdated();
    } catch (e) {
      setStepError('Failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    setStepError(null);
    try {
      await alertsAPI.skipStep(alertId, step.step_id);
      onStepUpdated();
    } catch (e) {
      setStepError('Skip failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rp-step-card" style={{
      borderLeft: `3px solid ${riskColor}`,
      opacity: step.status === 'skipped' ? 0.55 : 1
    }}>
      {/* Step header */}
      <div className="rp-step-header" onClick={() => setExpanded(!expanded)}>
        <div className="rp-step-left">
          <span className="rp-step-num">{step.sequence}</span>
          <span className="rp-step-icon">{ACTION_ICON[step.action_type] || '⚙️'}</span>
          <div>
            <div className="rp-step-title">{step.title}</div>
            <div className="rp-step-meta">
              <span style={{
                background: riskBg, color: riskColor,
                padding: '1px 6px', borderRadius: 8, fontSize: 11, fontWeight: 600
              }}>
                {step.risk_level} risk
              </span>
              {step.is_destructive && (
                <span style={{ color: 'var(--color-danger-500)', fontSize: 11, fontWeight: 600 }}>
                  ⚠ destructive
                </span>
              )}
              {isReadOnly && (
                <span style={{ color: 'var(--color-success-500)', fontSize: 11 }}>read-only</span>
              )}
            </div>
          </div>
        </div>
        <div className="rp-step-right">
          <StepBadge status={step.status} />
          <span className="rp-expand-icon">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="rp-step-body">
          {step.description && (
            <p className="rp-step-desc">{step.description}</p>
          )}

          {step.kubectl_command && (
            <div className="rp-command-block">
              <div className="rp-command-label">Command</div>
              <code className="rp-command">{step.kubectl_command}</code>
            </div>
          )}

          {step.expected_outcome && (
            <div className="rp-outcome">
              <span className="rp-outcome-label">Expected: </span>
              {step.expected_outcome}
            </div>
          )}

          {step.rollback_command && (
            <div className="rp-command-block rp-rollback">
              <div className="rp-command-label">Rollback</div>
              <code className="rp-command">{step.rollback_command}</code>
            </div>
          )}

          {/* Execution output */}
          {step.execution_output && (
            <div className="rp-output">
              <div className="rp-output-label">Output</div>
              <pre className="rp-output-pre">{step.execution_output}</pre>
            </div>
          )}

          {step.execution_error && (
            <div className="rp-error-box">
              <div className="rp-error-label">Error</div>
              <pre className="rp-error-pre">{step.execution_error}</pre>
            </div>
          )}

          {step.approved_by && (
            <div className="rp-audit-line">
              Approved by <strong>{step.approved_by}</strong>
              {step.approved_at && ` at ${new Date(step.approved_at).toLocaleString()}`}
            </div>
          )}

          {step.duration_ms != null && step.duration_ms > 0 && (
            <div className="rp-audit-line">
              Executed in {step.duration_ms}ms
            </div>
          )}

          {/* Action buttons */}
          {planStatus !== 'completed' && (
            <div className="rp-step-actions">
              {/* Manual step hint */}
              {isManual && ['pending', 'approved'].includes(step.status) && (
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6, fontStyle: 'italic', width: '100%' }}>
                  👤 This step requires manual action. Complete it yourself, then click Mark as Done.
                </div>
              )}

              {canApprove && (
                <button
                  className="rp-btn rp-btn-approve"
                  onClick={handleApprove}
                  disabled={loading}
                >
                  {loading ? 'Approving…' : '✓ Approve'}
                </button>
              )}

              {canMarkDone && (
                <button
                  className="rp-btn rp-btn-execute"
                  onClick={handleMarkDone}
                  disabled={loading}
                >
                  {loading ? 'Saving…' : '✓ Mark as Done'}
                </button>
              )}

              {canExecute && (
                <button
                  className={`rp-btn ${isReadOnly ? 'rp-btn-run' : 'rp-btn-execute'}`}
                  onClick={handleExecute}
                  disabled={loading || step.status === 'executing'}
                >
                  {loading || step.status === 'executing'
                    ? 'Running…'
                    : isReadOnly ? '▶ Run' : '▶ Execute'}
                </button>
              )}

              {canSkip && (
                <button
                  className="rp-btn rp-btn-skip"
                  onClick={handleSkip}
                  disabled={loading}
                >
                  Skip
                </button>
              )}

              {/* Inline error — replaces window.alert popups */}
              {stepError && (
                <div style={{ color: 'var(--color-danger-700)', fontSize: 12, marginTop: 6, width: '100%' }}>
                  {stepError}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const ResolutionPlan = ({ alert, onPlanUpdated }) => {
  const alertId = alert.id || alert.alert_id;
  const analysisComplete = alert.ai_analysis?.status === 'completed';

  const [plan, setPlan]             = useState(alert.resolution_plan || null);
  const [generating, setGenerating]       = useState(false);
  const [userContext, setUserContext]     = useState('');
  const [showContext, setShowContext]     = useState(false);
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [regenContext, setRegenContext]   = useState('');
  const [error, setError]               = useState(null);
  const pollRef                     = useRef(null);

  // Stop polling on unmount
  useEffect(() => {
    // If plan already generating, start polling immediately
    if (plan?.status === 'generating') {
      startPolling();
    }
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const startPolling = () => {
    let attempts = 0;
    const maxAttempts = 90; // 90s max

    const poll = async () => {
      try {
        const resp = await alertsAPI.getResolutionPlan(alertId);
        const fetchedPlan = resp.data.resolution_plan;
        setPlan(fetchedPlan);

        if (fetchedPlan.status === 'generating') {
          if (attempts < maxAttempts) {
            attempts++;
            pollRef.current = setTimeout(poll, 2000);
          } else {
            setError('Plan generation timed out. Please try again.');
            setGenerating(false);
          }
        } else {
          setGenerating(false);
          if (onPlanUpdated) onPlanUpdated();
        }
      } catch (e) {
        // 404 = plan cleared but not yet written by Celery — keep polling
        if (e.response?.status === 404 && attempts < maxAttempts) {
          attempts++;
          pollRef.current = setTimeout(poll, 2000);
        } else {
          setError('Failed to fetch plan: ' + (e.response?.data?.detail || e.message));
          setGenerating(false);
        }
      }
    };

    poll();
  };

  const handleGenerate = async (force = false) => {
    setGenerating(true);
    setError(null);
    try {
      await alertsAPI.generateResolutionPlan(alertId, userContext, force);
      setPlan({ status: 'generating' });
      setShowContext(false);
      startPolling();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to generate plan');
      setGenerating(false);
    }
  };

  const handleStepUpdated = async () => {
    try {
      const resp = await alertsAPI.getResolutionPlan(alertId);
      setPlan(resp.data.resolution_plan);
      if (onPlanUpdated) onPlanUpdated();
    } catch (e) {
      console.error('Failed to refresh plan:', e);
    }
  };

  const provenMatch = plan?.similar_resolutions_used?.find(r => r.proven);

  // ── Not started ─────────────────────────────────────────────────────────────
  if (!plan || plan.status === 'failed') {
    return (
      <div className="rp-container">
        <div className="rp-header">
          <h4 className="rp-title">Resolution Plan</h4>
          <span className="rp-subtitle">AI-powered step-by-step fix</span>
        </div>

        {plan?.status === 'failed' && (
          <div className="rp-error-box" style={{ marginBottom: 12 }}>
            Plan generation failed: {plan.error || 'Unknown error'}
          </div>
        )}

        {!analysisComplete && (
          <div className="rp-info-box">
            Run <strong>Troubleshoot</strong> and <strong>AI Analysis</strong> first for best results.
            You can still generate a plan without them.
          </div>
        )}

        {showContext && (
          <div style={{ marginBottom: 10 }}>
            <label className="rp-context-label">
              Additional context (optional)
            </label>
            <textarea
              value={userContext}
              onChange={e => setUserContext(e.target.value)}
              placeholder="e.g. This started after deploy v2.3.1 yesterday at 14:00. We rolled back but the issue persists."
              rows={2}
              className="rp-context-input"
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="rp-btn rp-btn-generate"
            onClick={() => handleGenerate(false)}
            disabled={generating}
          >
            {generating ? '⏳ Generating…' : '🤖 Generate Fix Plan'}
          </button>
          <button
            className="rp-btn rp-btn-link"
            onClick={() => setShowContext(!showContext)}
          >
            {showContext ? 'Hide context' : '+ Add context'}
          </button>
        </div>

        {error && <div className="rp-error-msg">{error}</div>}
      </div>
    );
  }

  // ── Generating ───────────────────────────────────────────────────────────────
  if (plan.status === 'generating') {
    return (
      <div className="rp-container">
        <div className="rp-header">
          <h4 className="rp-title">Resolution Plan</h4>
        </div>
        <div className="rp-loading">
          <div className="rp-spinner" />
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>SarvKube AI is generating a fix plan…</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              Analysing root cause, checking blast radius, ordering steps by risk level.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Plan ready / executing / completed / paused ───────────────────────────
  const steps       = plan.steps || [];
  const doneCount   = steps.filter(s => ['done', 'skipped'].includes(s.status)).length;
  const totalCount  = steps.length;
  const allDone     = plan.status === 'completed';
  const isPaused    = plan.status === 'paused';
  const overallRisk = plan.overall_risk || 'medium';

  return (
    <div className="rp-container">
      {/* Header */}
      <div className="rp-header">
        <div>
          <h4 className="rp-title">Resolution Plan</h4>
          <span className="rp-subtitle">
            Generated by SarvKube AI ·{' '}
            {plan.generated_at && new Date(plan.generated_at).toLocaleString()}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            background: RISK_BG[overallRisk],
            color: RISK_COLOR[overallRisk],
            padding: '3px 10px', borderRadius: 10,
            fontSize: 12, fontWeight: 700
          }}>
            {overallRisk.toUpperCase()} RISK
          </span>
          {allDone && (
            <span style={{ color: 'var(--color-success-500)', fontWeight: 700, fontSize: 13 }}>
              ✓ Complete
            </span>
          )}
          {isPaused && (
            <span style={{ color: 'var(--color-danger-500)', fontWeight: 700, fontSize: 13 }}>
              ⏸ Paused — step failed
            </span>
          )}
          <button
            className="rp-btn rp-btn-link"
            style={{ fontSize: 12 }}
            onClick={() => setShowRegenerate(r => !r)}
            disabled={generating}
            title="Regenerate plan with new context"
          >
            ↺ Regenerate
          </button>
        </div>
      </div>

      {/* Regenerate panel */}
      {showRegenerate && (
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Regenerate Plan</div>
          <textarea
            value={regenContext}
            onChange={e => setRegenContext(e.target.value)}
            placeholder="Optional: add context for the new plan e.g. 'Redis master port should be 6379 not 6380'"
            rows={2}
            className="rp-context-input"
            style={{ width: '100%', marginBottom: 8 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="rp-btn rp-btn-generate"
              disabled={generating}
              onClick={() => {
                setUserContext(regenContext);
                setShowRegenerate(false);
                handleGenerate(true);
              }}
            >
              {generating ? '⏳ Generating…' : '↺ Regenerate Plan'}
            </button>
            <button className="rp-btn rp-btn-link" onClick={() => setShowRegenerate(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Proven match banner */}
      {provenMatch && (
        <div className="rp-proven-banner">
          <span>⭐</span>
          <div>
            <strong>Proven Fix</strong> — this exact resolution worked{' '}
            <strong>{provenMatch.success_count}x</strong> before (similarity {(provenMatch.score * 100).toFixed(0)}%)
          </div>
        </div>
      )}

      {/* Blast radius */}
      {plan.blast_radius_assessment && (
        <div className="rp-blast-radius">
          <span className="rp-blast-label">Blast Radius:</span>
          {plan.blast_radius_assessment}
        </div>
      )}

      {/* Reasoning */}
      {plan.reasoning && (
        <div className="rp-reasoning">
          <span className="rp-reasoning-label">Approach:</span>
          {plan.reasoning}
        </div>
      )}

      {/* Progress bar */}
      {totalCount > 0 && (
        <div className="rp-progress">
          <div className="rp-progress-bar">
            <div
              className="rp-progress-fill"
              style={{ width: `${(doneCount / totalCount) * 100}%` }}
            />
          </div>
          <span className="rp-progress-label">{doneCount}/{totalCount} steps done</span>
        </div>
      )}

      {/* Steps */}
      <div className="rp-steps">
        {steps
          .slice()
          .sort((a, b) => a.sequence - b.sequence)
          .map(step => (
            <StepCard
              key={step.step_id}
              step={step}
              alertId={alertId}
              onStepUpdated={handleStepUpdated}
              planStatus={plan.status}
            />
          ))}
      </div>

      {/* Regenerate button */}
      {!allDone && (
        <div style={{ marginTop: 12 }}>
          <button
            className="rp-btn rp-btn-link"
            onClick={() => {
              setPlan(null);
              setError(null);
            }}
          >
            ↺ Regenerate plan
          </button>
        </div>
      )}

      {allDone && (
        <div className="rp-complete-banner">
          ✓ All steps completed. Don't forget to click <strong>Mark as Resolved</strong> above to save this to the knowledge base.
        </div>
      )}
    </div>
  );
};

export default ResolutionPlan;
